/**
 * @fileoverview Utility functions for handling address selection and formatting
 * @active This module is actively used across the application for address handling
 * @see {@link src/types/address.ts}
 * @see {@link src/components/personBy_Id/overview/AssociatedCompanies/AssociatedCompanies.tsx}
 * @see {@link src/utils/addressDeduplication.ts}
 * 
 * @note Future enhancement: This utility will be updated to support address deduplication,
 * integrating with the addressDeduplication.ts utility.
 * @note Address validation utilities have been added to support validation checks
 * in both client and server contexts.
 */

import {
  AddressSelectorParams,
  AddressColorResult,
  AddressData,
} from "@/types/address";

/**
 * Selects and formats an address based on provided parameters
 * @param params - Parameters for address selection
 * @returns Formatted address string or color-coded address object
 */
export const addressSelector = (params: {
  mainAddress?: any;
  data?: any[];
  type?: 'color' | 'simple' | null;
}): string | AddressColorResult => {
  const { mainAddress, data, type } = params;
  
  // Select the appropriate formatter function
  let creator: (data: any) => string | AddressColorResult;
  switch (type) {
    case "color":
      creator = createAddressColor;
      break;
    case "simple":
      creator = createAddressSimple;
      break;
    default:
      creator = createAddress;
  }

  // If a main address is provided, use it directly
  if (mainAddress) {
    return type === "color"
      ? createAddressColor(mainAddress)
      : createAddress(mainAddress);
  }

  // Otherwise select from the data array based on priority
  if (data?.length) {
    // Try to find address with isHq flag
    const hqAddress = data.find(el => el && el.isHq);
    if (hqAddress) return creator(hqAddress);
    
    // Try to find address with isPrimary flag
    const primaryAddress = data.find(el => el && el.isPrimary);
    if (primaryAddress) return creator(primaryAddress);
    
    // Try to find address with isCurrent flag
    const currentAddress = data.find(el => el && el.isCurrent);
    if (currentAddress) return creator(currentAddress);
    
    // Default to first address
    return creator(data[0]);
  }
  
  // No valid address found
  return "—";
};

/**
 * Creates a color-coded address object from address data
 * @param data - Address data to format
 * @returns Color-coded address object or dash if no data
 */
function createAddressColor(data: any): AddressColorResult | string {
  let result: AddressColorResult = {};
  
  // Add city if available
  if (data?.address?.city?.name) {
    result.city = { 
      name: data.address.city.name, 
      color: data.address.city.color || "gray.500" 
    };
  }
  
  // Add state if available
  if (data?.address?.state?.stateAbbrev) {
    result.state = {
      name: data.address.state.stateAbbrev,
      color: data.address.state.color || "gray.500",
    };
  }
  
  // Add country if available (using preferred code format)
  if (data?.address?.country?.countryCodeChar2) {
    result.country = {
      name: data.address.country.countryCodeChar2,
      color: data.address.country.color || "gray.500",
    };
  } else if (data?.address?.country?.countryCodeChar3) {
    result.country = {
      name: data.address.country.countryCodeChar3,
      color: data.address.country.color || "gray.500",
    };
  }
  
  // Return result if not empty, otherwise a dash
  return Object.keys(result).length > 0 ? result : "—";
}

/**
 * Creates a standard formatted address string from address data
 * @param data - Address data to format
 * @returns Formatted address string or dash if no data
 */
function createAddress(data: any): string {
  // Collect non-empty components
  const components = [
    data?.address?.city?.name,
    data?.address?.state?.stateAbbrev,
    data?.address?.country?.countryCodeChar2 || data?.address?.country?.countryCodeChar3,
  ].filter(Boolean);
  
  // Join components with comma, or return dash if empty
  return components.length > 0 ? components.join(", ") : "—";
}

/**
 * Creates a simplified address string from address data with fallbacks
 * @param data - Address data to format
 * @returns Simplified address string or dash if no data
 */
function createAddressSimple(data: any): string {
  // Collect non-empty components with fallbacks
  const components = [
    data?.address?.city?.name,
    data?.address?.state?.stateAbbrev || data?.address?.state?.name,
    data?.address?.country?.countryCodeChar2 || 
    data?.address?.country?.countryCodeChar3 || 
    data?.address?.country?.name,
  ].filter(Boolean);
  
  // Join components with comma, or return dash if empty
  return components.length > 0 ? components.join(", ") : "—";
}

/**
 * Validates an address for completeness and correctness
 * Returns validation details that can be used for reporting or UI feedback
 * 
 * @param addressData - Address data to validate
 * @param entity - Optional entity data for context
 * @returns Validation result with status and details
 */
export function validateAddress(addressData: AddressData, entity?: any) {
  if (!addressData || !addressData.address) {
    return {
      isValid: false,
      errorType: "INCOMPLETE_ADDRESS_DATA",
      message: "Missing address data structure",
      missingFields: ["address"],
      formatIssues: [],
      fieldStatus: {},
    };
  }
  
  const address = addressData.address;
  
  // Debug raw address in development
  if (process.env.NODE_ENV !== 'production') {
    console.log('validateAddress - raw address data:', address);
  }
  
  // Check for alternative field names
  const zipCode = (address as any).postalCode || address.geoLocationZipCode;
  const streetAddress = address.addressLine1 || address.street;

  const requiredFields = ['addressLine1', 'city', 'state', 'zipCode', 'country'] as const;

  // We'll track missing fields but won't necessarily consider the address invalid
  // if some are missing
  const missingFields = requiredFields.filter(field => {
    let value: any = null;
    
    // Safely access nested properties with fallbacks for different field names
    if (field === 'city') {
      value = address.city?.name;
    } else if (field === 'state') {
      value = address.state?.stateAbbrev || address.state?.name;
    } else if (field === 'country') {
      value = address.country?.name;
    } else if (field === 'addressLine1') {
      value = streetAddress;
    } else if (field === 'zipCode') {
      value = zipCode;
    } else {
      value = address[field as keyof typeof address];
    }
    
    // Check for null/undefined/empty string
    return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
  });

  const formatIssues: string[] = [];
  
  // Format validation for US addresses
  if (address.country?.name === 'United States') {
    // State validation
    if (address.state?.stateAbbrev && 
        (address.state.stateAbbrev.length !== 2 || !/^[A-Z]{2}$/.test(address.state.stateAbbrev))) {
      formatIssues.push(`state (${address.state.stateAbbrev})`);
    }
    
    // ZIP code validation 
    if (zipCode && !/^\d{5}(-\d{4})?$/.test(zipCode)) {
      formatIssues.push(`zipCode (${zipCode})`);
    }
  }
  
  // City validation for all countries
  if (address.city?.name && address.city.name.length <= 2) {
    formatIssues.push(`city (${address.city.name})`);
  }

  // Construct field status mapping for all required fields
  const fieldStatus: Record<string, boolean> = {};
  requiredFields.forEach(field => {
    if (field === 'city') {
      fieldStatus[field] = !!address.city?.name;
    } else if (field === 'state') {
      fieldStatus[field] = !!(address.state?.stateAbbrev || address.state?.name);
    } else if (field === 'country') {
      fieldStatus[field] = !!address.country?.name;
    } else if (field === 'addressLine1') {
      fieldStatus[field] = !!streetAddress;
    } else if (field === 'zipCode') {
      fieldStatus[field] = !!zipCode;
    } else {
      fieldStatus[field] = !!address[field as keyof typeof address];
    }
  });

  // Generate error message if issues found
  let errorMessage = '';
  if (missingFields.length > 0) {
    errorMessage += `Missing fields: ${missingFields.join(', ')}`;
  }
  if (formatIssues.length > 0) {
    if (errorMessage) errorMessage += '; ';
    errorMessage += `Format issues: ${formatIssues.join(', ')}`;
  }
  
  // For display purposes, we consider an address valid if it has at least
  // a city, or a state, or a country - something to display on a map
  const hasMinimumData = address.city?.name || 
                         address.state?.stateAbbrev || 
                         address.state?.name || 
                         address.country?.name ||
                         address.latitude || 
                         address.longitude ||
                         (streetAddress && zipCode);
  
  // An address with format issues is still displayable
  // Only report as invalid if it's completely missing displayable data
  const isValid = hasMinimumData;
  
  // Determine error type based on issues
  let errorType = "VALID_ADDRESS";
  if (!hasMinimumData) {
    errorType = "INCOMPLETE_ADDRESS_DATA";
  } else if (formatIssues.length > 0) {
    errorType = "MALFORMED_ADDRESS_DATA";
  }
  
  // Debug validation result
  if (process.env.NODE_ENV !== 'production') {
    console.log('validateAddress result:', {
      isValid,
      errorType,
      hasMinimumData,
      streetAddress,
      zipCode,
      city: address.city?.name,
      state: address.state?.stateAbbrev || address.state?.name,
      country: address.country?.name,
      missingFields,
      formatIssues
    });
  }
  
  return {
    isValid,
    errorType,
    message: errorMessage || "Address is valid",
    missingFields,
    formatIssues,
    fieldStatus,
    entityContext: entity ? {
      entityId: entity.id,
      entityName: entity.nameBrand,
    } : undefined
  };
}
